import { createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";

interface DataState {
	error: string | null;
	loaded: boolean;
	progress: number;
	assetCount: number;
	parties: Party[];
	offices: Office[];
	teams: Team[];
	agencies: Agency[];
	races: Race[];
	markets: Market[];
	// Commented out since ADMO is not currently being used
	//admoRaces: AdmoRace[];
	//admoSponsors: AdmoSponsor[];
	caucusRaces: CaucusRace[];
}

interface Party {
	id: number;
	full_name: string;
	short_name: string;
	color: string;
}

interface Office {
	id: number;
	full_name: string;
	short_name: string;
	sort_order: number;
	top_5: boolean;
	frequently_used: boolean;
}

interface Team {
	id: number;
	full_name: string;
	state: string;
	race_id: number;
}

interface Agency {
	id: number;
	full_name: string;
	short_name: string;
	logo: string;
	party_id: number;
}
export interface Race {
	id: number;
	race_type: string;
	race_name: string;
	jurisdiction: string;
	district: string;
	using_station_defaults: boolean;
	office: Office;
	party: Party | null;
	year: number;
}

interface CaucusRace {
	id: string; // {state}_{office_id}
	jurisdiction: string; // aka state
	office: Office;
	race_name: string;
}

interface Market {
	id: number;
	name: string;
}

/*
type AdmoRace = string;

interface AdmoSponsor {
	id: number;
	name: string;
}
*/

const INITIAL_STATE = {
	error: null,
	loaded: false,
	progress: 0,
	assetCount: 6,
	parties: [],
	offices: [],
	teams: [],
	agencies: [],
	races: [],
	markets: [],
	// Commented out since ADMO is not currently being used
	//admoRaces: [],
	//admoSponsors: [],
	caucusRaces: []
} as DataState;

function distinctCaucusRaces(arr: CaucusRace[]) {
	const seen = new Set();
	return arr.filter(({ race_name }) => {
		return seen.has(race_name)
			? false
			: (seen.add(race_name), true);
	});
}

export const slice = createSlice({
	name: "data",
	initialState: INITIAL_STATE,
	reducers: {
		supplyParties: (
			state: Draft<DataState>,
			action: PayloadAction<Party[]>
		) => {
			state.parties = action.payload;
			state.progress++;
		},
		supplyOffices: (
			state: Draft<DataState>,
			action: PayloadAction<Office[]>
		) => {
			state.offices = action.payload.sort((a, b) =>
				a.full_name.localeCompare(b.full_name)
			);
			state.progress++;
		},
		supplyTeams: (
			state: Draft<DataState>,
			action: PayloadAction<Team[]>
		) => {
			state.teams = action.payload.sort((a, b) =>
				a.full_name.localeCompare(b.full_name)
			);
			state.progress++;
		},
		supplyAgencies: (
			state: Draft<DataState>,
			action: PayloadAction<Agency[]>
		) => {
			state.agencies = action.payload.sort((a, b) =>
				a.full_name.localeCompare(b.full_name)
			);
			state.progress++;
		},
		supplyRaces: (
			state: Draft<DataState>,
			action: PayloadAction<Race[]>
		) => {
			state.races = action.payload.sort((a, b) =>
				a.race_name.localeCompare(b.race_name)
			);
			state.caucusRaces = distinctCaucusRaces(
				action.payload.map(r => ({
					id: `${r.jurisdiction}_${r.office.id}`,
					jurisdiction: r.jurisdiction,
					office_id: r.office.id,
					office: r.office,
					race_name: `${r.jurisdiction} - ${r.office.full_name}`
				}))
			).sort((a, b) =>
				a.race_name.localeCompare(b.race_name)
			);
			state.progress++;
		},
		supplyMarkets: (
			state: Draft<DataState>,
			action: PayloadAction<Market[]>
		) => {
			state.markets = action.payload
				.slice()
				.sort((a, b) => a.name.localeCompare(b.name));
			state.progress++;
		},
		setLoaded: (
			state: Draft<DataState>,
			action: PayloadAction<boolean>
		) => {
			state.loaded = action.payload;
		},
		setSupplyError: (
			state: Draft<DataState>,
			action: PayloadAction<string>
		) => {
			state.error = action.payload;
		}
	}
});

export default slice.reducer;
export const {
	supplyParties,
	supplyOffices,
	supplyTeams,
	supplyAgencies,
	supplyRaces,
	supplyMarkets,
	setLoaded,
	setSupplyError,
} = slice.actions;
